// Pure intent → visual params mapping for the Firefly visual prototype.
//
// "Intent" is the §9.4 / §9.7 concept from
// docs/planning/activity_dock_workflow_facilitation.md: a small set of
// expressive states the firefly can be in (Teaching / Guidance /
// Discovery / Focus / Critical Attention) that translate to distinct
// combinations of glow color, pulse rhythm, and dwell time. This
// module is the canonical mapping. Kept pure (no React, no DOM) so
// the lab + future production firefly + tests all share one source
// of truth.
//
// F1 (2026-06-05): all five intents specced.
// F3 (2026-06-17): V8 color lock — Discovery → blue, Focus →
//   purple/magenta (per owner). V3 pulse kinds + Critical jitter +
// of truth. Production home: src/components/activity/firefly/.

import {
  clampFireflyMotionSpeedMultiplier,
  FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
} from "@/lib/firefly/fireflyMotionSpeed";

export { FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER };

/** The five expressive intents the firefly can convey. Locked set
 *  per §9.4 of the activity-dock plan. */
export type FireflyIntent =
  | "teaching"
  | "guidance"
  | "discovery"
  | "focus"
  | "critical";

export type FireflyPulseKind = "sine" | "heartbeat";

/** Visual parameters for one intent. Concrete enough that a
 *  renderer can hand them straight to CSS without further
 *  interpretation. */
export type FireflyIntentVisualParams = {
  glowColor: string;
  glowDiameterPx: number;
  bodyDiameterPx: number;
  pulseDurationMs: number;
  pulseKind: FireflyPulseKind;
  /** One-shot bright pulse on entry (Teaching). */
  entryPulseMs?: number;
  dwellDurationMs: number;
  flickerDurationMs: number;
  /** Slow calm drift radius around the anchor (px). */
  hoverRadiusPx: number;
  hoverDurationMs: number;
  /** Critical-only position jitter (color-extension §5). */
  jitterRadiusPx?: number;
  jitterIntervalMs?: number;
};

function scaleDurationMs(ms: number, motionSpeedMultiplier: number): number {
  if (motionSpeedMultiplier <= 0) return ms;
  return Math.max(1, Math.round(ms / motionSpeedMultiplier));
}

/** Scale motion timings — durations ÷ multiplier; spatial sizes unchanged. */
export function applyFireflySpeedMultiplier(
  params: FireflyIntentVisualParams,
  motionSpeedMultiplier: number,
): FireflyIntentVisualParams {
  const speed = clampFireflyMotionSpeedMultiplier(motionSpeedMultiplier);
  if (speed === 1) return params;
  return {
    ...params,
    pulseDurationMs: scaleDurationMs(
      params.pulseDurationMs,
      speed,
    ),
    entryPulseMs:
      params.entryPulseMs === undefined
        ? undefined
        : scaleDurationMs(params.entryPulseMs, speed),
    dwellDurationMs: scaleDurationMs(
      params.dwellDurationMs,
      speed,
    ),
    flickerDurationMs: scaleDurationMs(
      params.flickerDurationMs,
      speed,
    ),
    hoverDurationMs: scaleDurationMs(
      params.hoverDurationMs,
      speed,
    ),
    jitterIntervalMs:
      params.jitterIntervalMs === undefined
        ? undefined
        : scaleDurationMs(params.jitterIntervalMs, speed),
  };
}

export function getFireflyIntentParamsWithMotionSpeed(
  intent: FireflyIntent,
  motionSpeedMultiplier: number = FIREFLY_DEFAULT_MOTION_SPEED_MULTIPLIER,
): FireflyIntentVisualParams | null {
  const base = getFireflyIntentParams(intent);
  if (!base) return null;
  return applyFireflySpeedMultiplier(base, motionSpeedMultiplier);
}

export function getFireflyIntentParams(
  intent: FireflyIntent,
): FireflyIntentVisualParams | null {
  switch (intent) {
    case "discovery":
      return {
        glowColor: "rgba(96, 165, 250, 0.85)",
        glowDiameterPx: 20,
        bodyDiameterPx: 6,
        pulseDurationMs: 2400,
        pulseKind: "sine",
        dwellDurationMs: 6000,
        flickerDurationMs: 600,
        hoverRadiusPx: 5,
        hoverDurationMs: 9000,
      };
    case "teaching":
      return {
        glowColor: "rgba(52, 211, 153, 0.9)",
        glowDiameterPx: 20,
        bodyDiameterPx: 6,
        pulseDurationMs: 1600,
        pulseKind: "sine",
        entryPulseMs: 550,
        dwellDurationMs: 4000,
        flickerDurationMs: 500,
        hoverRadiusPx: 6,
        hoverDurationMs: 7500,
      };
    case "guidance":
      return {
        glowColor: "rgba(251, 191, 36, 0.9)",
        glowDiameterPx: 20,
        bodyDiameterPx: 6,
        pulseDurationMs: 2000,
        pulseKind: "sine",
        dwellDurationMs: 5000,
        flickerDurationMs: 600,
        hoverRadiusPx: 5,
        hoverDurationMs: 8500,
      };
    case "focus":
      return {
        glowColor: "rgba(192, 132, 252, 0.82)",
        glowDiameterPx: 20,
        bodyDiameterPx: 6,
        pulseDurationMs: 3000,
        pulseKind: "sine",
        dwellDurationMs: 8000,
        flickerDurationMs: 800,
        hoverRadiusPx: 4,
        hoverDurationMs: 11000,
      };
    case "critical":
      return {
        glowColor: "rgba(248, 113, 113, 0.95)",
        glowDiameterPx: 24,
        bodyDiameterPx: 7,
        pulseDurationMs: 700,
        pulseKind: "heartbeat",
        dwellDurationMs: 1500,
        flickerDurationMs: 400,
        hoverRadiusPx: 3,
        hoverDurationMs: 6000,
        jitterRadiusPx: 5,
        jitterIntervalMs: 4000,
      };
  }
}
